import React, { MouseEventHandler, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import axios from "axios";
import { NodeModel } from "@minoru/react-dnd-treeview";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { TypeIcon } from "./TypeIcon";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import { useParams } from "react-router";
import { RootState } from "../../../app/store";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import {
  deleteNewFolder,
  stopEditingFolder,
  startEditingFolder,
  saveFolder,
  deleteFolder,
} from "../../../app/promptsSlice";
import { apiBaseURL } from "../../../app/utils";

type Props = {
  node: NodeModel<any>;
  depth: number;
  isOpen: boolean;
  onToggle: (id: NodeModel["id"]) => void;
};

function FolderMenu({
  onDelete,
  onRename,
  onDownload,
}: {
  onDelete: MouseEventHandler;
  onRename: MouseEventHandler;
  onDownload: MouseEventHandler;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setAnchorEl(null);
    onDelete(e);
  };

  const handleRename = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setAnchorEl(null);
    onRename(e);
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        sx={{ p: 0.2 }}
      >
        <MoreVertIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock={true}
      >
        <MenuItem onClick={handleRename}>Rename</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>
    </div>
  );
}

export const Node: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const { droppable, data, id, text, parent } = props.node;
  const [newFolderName, setNewFolderName] = useState<string>(text);
  const isDeleting = useAppSelector(
    (state: RootState) => state.prompts.isDeletingFolder
  );
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

  const handleCloseDeletePromptDialog = () => {
    setOpenDeleteDialog(false);
  };
  const handleDeleteFolder = () => {
    setOpenDeleteDialog(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(deleteFolder(id));
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiBaseURL}/folders/${id}`, {
        headers: {
          Authorization: "Bearer " + token,
        },
        responseType: "blob", // important
      });
      // create file link in browser's memory
      const href = URL.createObjectURL(response.data);

      // create "a" HTML element with href to file & click
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", "folder.json"); //or any other extension
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error: any) {
      alert(error);
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (data?.isNew) return;
    props.onToggle(id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setNewFolderName(text);
    dispatch(startEditingFolder(id));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      if (newFolderName !== "") {
        if (newFolderName === text) dispatch(stopEditingFolder(id));
        else
          dispatch(
            saveFolder({
              id: id.toString(),
              name: newFolderName,
              parentFolderId: parent,
            })
          );
      }
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setNewFolderName(event.target.value);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (newFolderName === "") {
      if (data?.isNew) dispatch(deleteNewFolder(id));
      else if (data?.isEdited) dispatch(stopEditingFolder(id));
    } else {
      if (newFolderName === text) dispatch(stopEditingFolder(id));
      else
        dispatch(
          saveFolder({
            id: id.toString(),
            name: newFolderName,
            parentFolderId: parent,
          })
        );
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeletePromptDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        disableScrollLock={true}
      >
        <DialogTitle id="alert-dialog-title">Deleting This Folder</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You're about to delete this folder, <b>the content of this folder
            will be deleted as well</b>, are you sure you want to delete this
            folder?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={isDeleting} onClick={handleCloseDeletePromptDialog}>
            Cancel
          </Button>
          <Button
            disabled={isDeleting}
            color="error"
            onClick={handleDelete}
            autoFocus
          >
            Delete Folder
          </Button>
        </DialogActions>
      </Dialog>
      {droppable ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pt: 0.5,
            pb: 0.5,
            pl: props.depth * 3,
            pr: 0.2,
            width: "100%",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              cursor: "pointer",
              transformOrigin: "center",
              transition: "transform linear 0.1s",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              transform: props.isOpen ? "rotate(90deg)" : "",
            }}
            onClick={handleToggle}
          >
            <ArrowRightIcon />
          </Box>
          <TypeIcon droppable={!!droppable} fileType={data?.fileType} />
          {data?.isEdited ? (
            <input
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              value={newFolderName}
              autoFocus
              style={{
                width: "100%",
                marginLeft: "4px",
                marginRight: "2px",
              }}
            />
          ) : (
            <Typography
              onDoubleClick={handleEdit}
              variant="caption"
              sx={{ ml: 0.5, flexGrow: 1 }}
            >
              {text}
            </Typography>
          )}
          {!data?.isEdited && (
            <FolderMenu
              onDelete={handleDeleteFolder}
              onRename={handleEdit}
              onDownload={handleDownload}
            />
          )}
        </Box>
      ) : (
        <Link
          underline="none"
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            "&:hover": {
              backgroundColor: "rgba(256,256,256, 0.1)",
            },
            backgroundColor:
              params.userPromptId === props.node.id
                ? "rgba(256,256,256, 0.1)"
                : "",
            textAlign: "left",
            pt: 0.5,
            pb: 0.5,
            pl: props.depth * 3 || 0.5,
            pr: 0.5,
          }}
          component={RouterLink}
          color="#fff"
          to={`/edit/${id}`}
        >
          <TypeIcon droppable={!!droppable} fileType={data?.fileType} />
          <Typography variant="caption" sx={{ ml: 0.5 }}>
            {text}
          </Typography>
        </Link>
      )}
    </Box>
  );
};
